import { useCallback, useEffect, useState } from 'react';
import { Navigate, useOutletContext, useParams } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';
import { AxiosError } from 'axios';

import AddIcon from '@mui/icons-material/Add';
import { Box, Theme, Typography } from '@mui/material';

import {
  deleteLienRecovery,
  getLienRecoveries,
  getLienRecoveryDocument,
  LienRecoveriesResponse,
  LienRecovery,
} from '../../../api/paymentsApi/deductionsApi';
import CommonButton from '../../../components/buttons/CommonButton';
import LienRecoveryPopup from '../../../components/LienRecoveryPopup';
import Loader from '../../../components/Loader';
import NoDataText from '../../../components/NoDataText';
import Popup from '../../../components/Popup';
import TableContainer from '../../../components/Table/TableContainer';
import { DEDUCTION_RECOVERIES_TABLE_LABELS } from '../../../constants/tableLabels';
import { numberToUSDCurrency } from '../../../helpers/numberToUSDCurrency';
import { useHasPermission } from '../../../hooks/useHasPermission';
import usePagination from '../../../hooks/usePagination';
import useSnackBar from '../../../hooks/useSnackBar';
import { ActiveSortField, EntityFlexibleRecord, ErrorData } from '../../../types';

import DeductionRecoveriesTableDataRow from './DeductionRecoveriesTableDataRow';

const useStyles = makeStyles()((theme: Theme) => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
    flexWrap: 'wrap',
    marginBottom: '16px',
  },
  summary: {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap',
  },
  summaryItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    padding: '12px 16px',
    borderRadius: '8px',
    backgroundColor: theme.palette.primary.light,
    minWidth: '180px',
  },
  summaryLabel: {
    fontFamily: 'Inter',
    fontSize: '12px',
    color: theme.palette.text.disabled,
  },
  summaryValue: {
    fontFamily: 'Inter',
    fontSize: '16px',
    fontWeight: 600,
    color: theme.palette.common.white,
  },
}));

const DeductionsRecovery = () => {
  const { classes } = useStyles();
  const { deductionId } = useParams();
  const { entityData } = useOutletContext<EntityFlexibleRecord>();
  const { setAlert } = useSnackBar();
  const { pageNumber, rowsPerPage, setTotalCount } = usePagination();
  const canEdit = useHasPermission('Payments', 'Edit');

  const [recoveryData, setRecoveryData] = useState<LienRecoveriesResponse>();
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [activeSortField, setActiveSortField] = useState<ActiveSortField | null>(null);
  const [isCreatePopupOpen, setIsCreatePopupOpen] = useState(false);
  const [recoveryToEdit, setRecoveryToEdit] = useState<LienRecovery | null>(null);
  const [recoveryIdToDelete, setRecoveryIdToDelete] = useState<number | null>(null);

  // Recoveries can be recorded only on Lien and Service Provider deductions
  const canCreateRecovery =
    canEdit && (entityData?.type === 'Lien' || entityData?.type === 'SERVICE_PROVIDER');

  const showError = (error: unknown, fallbackMessage: string) => {
    const axiosError = error as AxiosError<ErrorData>;
    setAlert((prev) => ({
      ...prev,
      message: axiosError?.response?.data?.message || fallbackMessage,
      type: 'error',
    }));
  };

  const fetchRecoveries = useCallback(() => {
    if (!deductionId) return;

    setIsDataLoading(true);
    getLienRecoveries(deductionId)
      .then((res) => {
        setRecoveryData(res.data);
        setTotalCount?.(res.data.recoveries?.length || 0);
      })
      .catch((error) => showError(error, 'Failed to load recoveries'))
      .finally(() => setIsDataLoading(false));
  }, [deductionId]);

  useEffect(() => {
    fetchRecoveries();
  }, [fetchRecoveries]);

  const handleDownload = (recoveryId: number) => {
    if (!deductionId) return;

    getLienRecoveryDocument(deductionId, recoveryId)
      .then((res) => window.open(res.data.downloadUrl, '_blank'))
      .catch((error) => showError(error, 'Failed to download document'));
  };

  const handleDelete = () => {
    if (!deductionId || recoveryIdToDelete === null) return;

    deleteLienRecovery(deductionId, recoveryIdToDelete)
      .then(() => {
        setAlert((prev) => ({
          ...prev,
          message: 'Recovery deleted successfully.',
          type: 'success',
        }));
        fetchRecoveries();
      })
      .catch((error) => showError(error, 'Failed to delete recovery'))
      .finally(() => setRecoveryIdToDelete(null));
  };

  // The tab is only offered for paid Lien and Service Provider deductions; guard direct URL access too
  if (
    entityData &&
    (entityData.status !== 'Paid' || (entityData.type !== 'Lien' && entityData.type !== 'SERVICE_PROVIDER'))
  ) {
    return <Navigate to="../home" replace />;
  }

  // The API returns every recovery, so page through them on the client
  const recoveries = recoveryData?.recoveries ?? [];
  const pagedRecoveries = recoveries.slice((pageNumber - 1) * rowsPerPage, pageNumber * rowsPerPage);

  return (
    <>
      {isDataLoading && !recoveryData ? (
        <Box position="absolute" top="50%" left="50%">
          <Loader colorType="warning" />
        </Box>
      ) : (
        <>
          <Box className={classes.header}>
            <Box className={classes.summary}>
              <Box className={classes.summaryItem}>
                <Typography className={classes.summaryLabel}>Paid</Typography>
                <Typography className={classes.summaryValue}>
                  {numberToUSDCurrency(recoveryData?.deductionAmount ?? (Number(entityData?.amount) || 0))}
                </Typography>
              </Box>
              <Box className={classes.summaryItem}>
                <Typography className={classes.summaryLabel}>Total recovered</Typography>
                <Typography className={classes.summaryValue}>
                  {numberToUSDCurrency(recoveryData?.totalRecovered ?? 0)}
                </Typography>
              </Box>
              <Box className={classes.summaryItem}>
                <Typography className={classes.summaryLabel}>Remaining recoverable</Typography>
                <Typography className={classes.summaryValue}>
                  {recoveryData ? numberToUSDCurrency(recoveryData.remainingRecoverable) : '-'}
                </Typography>
              </Box>
            </Box>

            {canCreateRecovery && (
              <CommonButton
                type="infoMain"
                buttonText="Create Recovery"
                buttonIcon={<AddIcon />}
                disabled={!recoveryData || recoveryData.remainingRecoverable <= 0}
                onButtonClick={() => setIsCreatePopupOpen(true)}
              />
            )}
          </Box>

          {!isDataLoading && recoveries.length === 0 ? (
            <NoDataText />
          ) : (
            <TableContainer
              headerLabels={DEDUCTION_RECOVERIES_TABLE_LABELS}
              activeSortField={activeSortField}
              setActiveSortField={setActiveSortField}
            >
              <DeductionRecoveriesTableDataRow
                tableData={pagedRecoveries}
                canEdit={canEdit}
                onEdit={setRecoveryToEdit}
                onDownload={handleDownload}
                onDelete={setRecoveryIdToDelete}
              />
            </TableContainer>
          )}
        </>
      )}

      {deductionId && (
        <LienRecoveryPopup
          isOpen={isCreatePopupOpen || recoveryToEdit !== null}
          lien={{
            id: deductionId,
            providerName: (entityData?.vendorAccount as { label?: string } | null)?.label || '-',
            paidAmount: Number(entityData?.amount) || 0,
            paidAt: entityData?.paidAt as string | null,
          }}
          recovery={recoveryToEdit}
          onSuccess={() => {
            setIsCreatePopupOpen(false);
            setRecoveryToEdit(null);
            fetchRecoveries();
          }}
          onCancel={() => {
            setIsCreatePopupOpen(false);
            setRecoveryToEdit(null);
          }}
        />
      )}

      <Popup
        isOpen={recoveryIdToDelete !== null}
        headlineText="Delete recovery"
        contentText="Are you sure you want to delete this recovery? This cannot be undone."
        mainButtonText="Delete"
        secondaryButtonText="Cancel"
        onMainButtonClick={handleDelete}
        onSecondaryButtonClick={() => setRecoveryIdToDelete(null)}
      />
    </>
  );
};

export default DeductionsRecovery;
